import { useCallback, useMemo, useState } from "react";
import { Button, Input } from "@material-tailwind/react";
import Switch from "../../../components/switch/Switch";
import DynamicListSelect from "../../../components/data/dynamic-list/DynamicListSelect";
import { openNativePicker } from "../../../components/data/date/openNativePicker";
import { api } from "../../../services/api/api";
import { format, setWeek, setYear } from "date-fns";

type PeriodType = "week" | "month";
type WorkorderStatus = "CLOSED" | "VALIDATED" | "INVOICED" | "PAID";

interface PartsExportRow {
  interventionReference: string;
  interventionStatus: string;
  technicianName: string;
  hoseName: string;
  hoseReference: string;
  partName: string;
  partReference: string;
  partQuantity: string | number;
  unitOfMeasure: string;
  desk?: boolean;
}

export default function PartsExportForm() {
  const [periodType, setPeriodType] = useState<{
    value: PeriodType;
    label: string;
  } | null>({ value: "week", label: "Week" });
  const [periodValue, setPeriodValue] = useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [includeDesk, setIncludeDesk] = useState<boolean>(true);
  const defaultStatuses = useMemo<WorkorderStatus[]>(
    () => ["CLOSED", "VALIDATED", "INVOICED", "PAID"],
    []
  );

  const canExport = useMemo(() => {
    return Boolean(periodType?.value) && Boolean(periodValue);
  }, [periodType, periodValue]);

  const handleExport = useCallback(async () => {
    if (!canExport || !periodType) return;
    try {
      setIsLoading(true);
      const upperType = periodType.value.toUpperCase(); // WEEK or MONTH
      const payload: any = { periodType: upperType, statuses: defaultStatuses };

      if (periodType.value === "week") {
        // Expected input: YYYY-Www
        const [yearStr, weekStrWithW] = periodValue.split("-W");
        payload.year = parseInt(yearStr, 10);
        payload.week = parseInt(weekStrWithW, 10);
      } else if (periodType.value === "month") {
        // Expected input: YYYY-MM
        const [yearStr, monthStr] = periodValue.split("-");
        payload.year = parseInt(yearStr, 10);
        payload.month = parseInt(monthStr, 10);
      }

      const result = (await api.post(
        "admin/workorders/parts/export",
        { ...payload, includeDesk }
      )) as PartsExportRow[];

      const headers = [
        "Intervention Reference",
        "Intervention Status",
        "Technician's Name",
        "Hose Name",
        "Hose Reference",
        "Part Name (label)",
        "Part Reference",
        "Part Quantity",
        "Unit of Measure",
      ];

      const escapeCsv = (val: unknown) => {
        const str = (val ?? "").toString();
        const needsQuotes = /[",\n]/.test(str);
        const escaped = str.replace(/"/g, "\"\"");
        return needsQuotes ? `"${escaped}"` : escaped;
      };

      const filtered = includeDesk ? result : result.filter((r) => !r.desk);
      const rows = filtered.map((r) => [
        r.interventionReference,
        r.interventionStatus,
        r.technicianName,
        r.hoseName,
        r.hoseReference,
        r.partName,
        r.partReference,
        r.partQuantity,
        r.unitOfMeasure,
      ]);

      const csvContent = [headers, ...rows]
        .map((line) => line.map(escapeCsv).join(","))
        .join("\n");

      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      // Build filename: Parts consumption from {DD/MM/YYYY} to {DD/MM/YYYY}
      let fromDate: Date;
      let toDate: Date;
      if (periodType.value === "week") {
        const [yearStr, weekStrWithW] = periodValue.split("-W");
        fromDate = new Date();
        fromDate = setYear(fromDate, parseInt(yearStr, 10));
        fromDate = setWeek(fromDate, parseInt(weekStrWithW, 10), { weekStartsOn: 1 });
        toDate = new Date(fromDate);
        toDate.setDate(toDate.getDate() + 6);
      } else {
        const [yearStr, monthStr] = periodValue.split("-");
        const y = parseInt(yearStr, 10);
        const m = parseInt(monthStr, 10);
        fromDate = new Date(y, m - 1, 1);
        toDate = new Date(y, m, 0);
      }
      const fromStr = format(fromDate, "dd-MM-yyyy");
      const toStr = format(toDate, "dd-MM-yyyy");
      a.download = `Parts consumption from ${fromStr} to ${toStr}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Export parts error:", err);
    } finally {
      setIsLoading(false);
    }
  }, [canExport, periodType, periodValue, defaultStatuses, includeDesk]);

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="flex items-center justify-between gap-x-3">            
          <label className="text-sm font-medium text-gray-700 text-right">
            Type of period
          </label>
          <div className="flex-1 max-w-[65%]">
            <DynamicListSelect<{ value: PeriodType; label: string }>
              data={periodType as any}
              ressource="period-types-week-month"
              onChange={(v) => setPeriodType(v as any)}
              placeholder="Select period type"
            />
          </div>
        </div>
        <div className="flex items-center justify-between gap-x-3 mt-3">
          <label className="text-sm font-medium text-gray-700 text-right">
            Period to export
          </label>
          <div className="flex-1 max-w-[65%]">
            {periodType?.value === "week" && (
              <Input
                type="week"
                value={periodValue}
                onClick={openNativePicker}
                onChange={(e) => setPeriodValue(e.target.value)}
              />
            )}
            {periodType?.value === "month" && (
              <Input
                type="month"
                value={periodValue}
                onClick={openNativePicker}
                onChange={(e) => setPeriodValue(e.target.value)}
              />
            )}
          </div>
        </div>

        <div className="flex items-center justify-between gap-x-3 mt-3">
          <label className="text-sm font-medium text-gray-700 text-right">
            Include desk parts
          </label>
          <div className="flex-1 max-w-[65%]">
            <div className="flex">
              <Switch checked={includeDesk} onChange={setIncludeDesk} />
            </div>
          </div>
        </div>
      </div>
      
      <div className="pt-2">
        <Button
          color="blue"
          className="w-full"
          disabled={!canExport || isLoading}
          onClick={handleExport}
        >
          {isLoading ? "Exporting..." : "Export parts (CSV)"}
        </Button>
      </div>
    </div>
  );
}
